import { StripePostalAddress } from '@commutifi/models/shared/StripePostalAddress'
import { format, CommonOptions } from '@fragaria/address-formatter'
import { BuildingFilter } from 'api/modules/analytics'
import { Address } from 'shared/interfaces'

const isStripePostalAddress = (address: Partial<Address> | StripePostalAddress): address is StripePostalAddress =>
  Object.prototype.hasOwnProperty.call(address, 'line1')
/**
 * See \@fragaria/address-formatter
 * Inline is the default format and you can pass
 * an option to display a multiline address (format: array).
 * To add the country use option country (appendCountry: true)
 * For more details please see their doc https://github.com/fragaria/address-formatter
 */
export function formatAddress<T extends 'array' | 'string' = 'string'>(
  address: Partial<Address> | StripePostalAddress | undefined,
  options: CommonOptions & { output?: T } = {}
): T extends 'array' ? string[] : string {
  if (!address) {
    // If the address is undefined, return an empty string or empty array based on the expected return type
    return (options.output === 'array' ? [''] : '') as T extends 'array' ? string[] : string
  }

  const normalizedAddress: Partial<Address> = isStripePostalAddress(address)
    ? {
        street: address.line1,
        city: address.city || '',
        postalCode: address.postal_code || '',
        country: address.country,
        state: address.state || ''
      }
    : address
  return format(
    {
      houseNumber: normalizedAddress.streetNumber,
      road: normalizedAddress.street,
      city: normalizedAddress.city,
      postcode: normalizedAddress.postalCode,
      state: normalizedAddress.state,
      countryCode: normalizedAddress.country
    },
    // They have 2 time the same function (overwrite of params) defined which confuses typescript
    // @ts-expect-error -- Function overwrites confuses typescript
    { output: 'string', fallbackCountryCode: 'US', ...options }
  )
}

export const formatBuildingNameOrAddress = (building: BuildingFilter | undefined) =>
  building?.name ??
  formatAddress(
    {
      street: building?.street,
      streetNumber: building?.streetNumber,
      postalCode: building?.postalCode,
      city: building?.city,
      state: building?.state,
      country: building?.country
    },
    { output: 'array' }
  )
